/**
 * Account Holdings Modal
 * WP-MANUAL-HOLD-2: Modal for viewing and managing holdings for a specific account
 *
 * For investment accounts: Shows real holdings with CRUD capabilities
 * For non-investment accounts: Shows synthetic holding derived from balance
 */
import { useState } from 'react';
import { useAccountHoldings, useDeleteHolding } from '@/hooks/useAllocation';
import Button from '@/components/ui/Button';
import { formatCurrency } from '@/utils/format';
import {
  ASSET_CLASS_DISPLAY_NAMES,
  type HoldingWithClassification,
} from '@/types/allocation';
import type { AccountWithConnection } from '@/services/accounts';
import ManageHoldingModal from '@/components/allocation/ManageHoldingModal';
import ClassifyHoldingModal from '@/components/allocation/ClassifyHoldingModal';

interface AccountHoldingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  account: AccountWithConnection | null;
}

export default function AccountHoldingsModal({
  isOpen,
  onClose,
  account,
}: AccountHoldingsModalProps) {
  const [showAddHolding, setShowAddHolding] = useState(false);
  const [editHoldingId, setEditHoldingId] = useState<string | null>(null);
  const [classifyHoldingId, setClassifyHoldingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Fetch holdings for this account
  const { data: holdingsData, isLoading } = useAccountHoldings(
    account?.id ?? null,
    true // include synthetic holdings
  );

  const { mutate: deleteHolding, isPending: isDeleting } = useDeleteHolding();

  if (!isOpen || !account) return null;

  const isInvestmentAccount = account.type === 'investment';
  const isManualAccount = account.sourceType === 'manual';
  const canAddHoldings = isInvestmentAccount && isManualAccount;

  const holdings = holdingsData?.holdings ?? [];

  const handleDelete = (holdingId: string) => {
    deleteHolding(holdingId, {
      onSuccess: () => {
        setDeleteConfirmId(null);
      },
    });
  };

  const handleClose = () => {
    setShowAddHolding(false);
    setEditHoldingId(null);
    setClassifyHoldingId(null);
    setDeleteConfirmId(null);
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={handleClose}
          aria-hidden="true"
        />

        {/* Modal */}
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative w-full max-w-2xl transform bg-white rounded-lg shadow-xl transition-all">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {account.name} - Holdings
                </h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {isInvestmentAccount ? (
                    <>Investment account holdings</>
                  ) : (
                    <>Balance shown as synthetic holding</>
                  )}
                </p>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <CloseIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
              {isLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="animate-pulse">
                      <div className="h-16 bg-gray-200 rounded-lg" />
                    </div>
                  ))}
                </div>
              ) : holdings.length === 0 ? (
                <div className="text-center py-8">
                  <HoldingsIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No holdings
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {canAddHoldings
                      ? 'Add your first holding to start tracking your investments.'
                      : 'This account has no balance to display.'}
                  </p>
                  {canAddHoldings && (
                    <div className="mt-4">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setShowAddHolding(true)}
                      >
                        Add Holding
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {holdings.map((holding) => (
                    <HoldingRow
                      key={holding.holdingId}
                      holding={holding}
                      currency={account.currency}
                      canEdit={!holding.isSynthetic && holding.isManual === true}
                      canDelete={!holding.isSynthetic && holding.isManual === true}
                      canClassify={true}
                      isDeleteConfirming={deleteConfirmId === holding.holdingId}
                      isDeleting={isDeleting && deleteConfirmId === holding.holdingId}
                      onEdit={() => setEditHoldingId(holding.holdingId)}
                      onDelete={() => setDeleteConfirmId(holding.holdingId)}
                      onCancelDelete={() => setDeleteConfirmId(null)}
                      onConfirmDelete={() => handleDelete(holding.holdingId)}
                      onClassify={() => setClassifyHoldingId(holding.holdingId)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
              <div className="text-sm text-gray-500">
                {holdings.length > 0 && (
                  <>
                    {holdings.length} holding{holdings.length !== 1 ? 's' : ''} |
                    Total: {formatCurrency(
                      holdings.reduce((sum, h) => sum + parseFloat(h.currentValue || '0'), 0),
                      account.currency
                    )}
                  </>
                )}
              </div>
              <div className="flex gap-3">
                {canAddHoldings && holdings.length > 0 && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowAddHolding(true)}
                  >
                    Add Holding
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={handleClose}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sub-modals */}
      <ManageHoldingModal
        isOpen={showAddHolding}
        onClose={() => setShowAddHolding(false)}
        accountId={account.id}
      />

      <ManageHoldingModal
        isOpen={!!editHoldingId}
        onClose={() => setEditHoldingId(null)}
        holdingId={editHoldingId ?? undefined}
      />

      <ClassifyHoldingModal
        isOpen={!!classifyHoldingId}
        onClose={() => setClassifyHoldingId(null)}
        holdingId={classifyHoldingId}
      />
    </>
  );
}

interface HoldingRowProps {
  holding: HoldingWithClassification;
  currency: string;
  canEdit: boolean;
  canDelete: boolean;
  canClassify: boolean;
  isDeleteConfirming: boolean;
  isDeleting: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
  onClassify: () => void;
}

function HoldingRow({
  holding,
  currency,
  canEdit,
  canDelete,
  canClassify,
  isDeleteConfirming,
  isDeleting,
  onEdit,
  onDelete,
  onCancelDelete,
  onConfirmDelete,
  onClassify,
}: HoldingRowProps) {
  return (
    <div className="p-3 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
      <div className="flex items-start gap-3">
        {/* Holding info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-gray-900 truncate">
              {holding.securityName}
            </h4>
            {holding.ticker && (
              <span className="text-xs text-gray-500 font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                {holding.ticker}
              </span>
            )}
            <SourceBadge holding={holding} />
          </div>
          <div className="mt-1 flex items-center gap-3 text-sm text-gray-500">
            <span>{parseFloat(holding.quantity).toLocaleString()} units</span>
            <span className="text-gray-300">|</span>
            <button
              onClick={onClassify}
              className="hover:text-blue-600 hover:underline transition-colors"
              title="Change asset class"
            >
              {ASSET_CLASS_DISPLAY_NAMES[holding.assetClass]}
            </button>
            {holding.needsReview && (
              <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                <WarningIcon className="h-3.5 w-3.5" />
                Needs review
              </span>
            )}
          </div>
        </div>

        {/* Value and actions */}
        <div className="flex items-center gap-2">
          <div className="text-right">
            <p className="font-semibold text-gray-900 tabular-nums">
              {formatCurrency(parseFloat(holding.currentValue || '0'), currency)}
            </p>
          </div>

          {/* Action buttons */}
          {!isDeleteConfirming && (
            <div className="flex items-center gap-1">
              {canClassify && (
                <button
                  onClick={onClassify}
                  className="p-1.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all"
                  title="Classify"
                >
                  <TagIcon className="h-4 w-4" />
                </button>
              )}
              {canEdit && (
                <button
                  onClick={onEdit}
                  className="p-1.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all"
                  title="Edit"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
              )}
              {canDelete && (
                <button
                  onClick={onDelete}
                  className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all"
                  title="Delete"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          )}

          {/* Delete confirmation */}
          {isDeleteConfirming && (
            <div className="flex items-center gap-2">
              <Button
                variant="danger"
                size="sm"
                onClick={onConfirmDelete}
                isLoading={isDeleting}
              >
                Delete
              </Button>
              <Button variant="ghost" size="sm" onClick={onCancelDelete} disabled={isDeleting}>
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SourceBadge({ holding }: { holding: HoldingWithClassification }) {
  if (holding.isSynthetic) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
        Balance-derived
      </span>
    );
  }
  if (holding.isManual) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
        Manual
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
      Synced
    </span>
  );
}

// Icons
function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function HoldingsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
    </svg>
  );
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
    </svg>
  );
}

function TagIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  );
}
